import { useEffect, useState, useCallback, useRef } from "react"
import styled from "styled-components"
import AdminService from "../../../services/admin.service"

interface Session {
    session_id: string; nombre?: string; correo?: string; paso_actual: number;
    requiere_humano: boolean; motivo_escalada?: string; escalada_en?: string;
    atendida_por?: string; fecha_ultimo_msg?: string;
    origen?: string; wa_id?: string;
}
interface Msg { role: string; nombre_agente?: string; contenido: string; archivo?: string; timestamp?: string }

// Los archivos que el pasajero manda por WhatsApp se descargan en el server y
// se sirven por /api/files, igual que los comprobantes del chat web.
const urlArchivo = (archivo: string) => `${process.env.REACT_APP_API_URL}/files/${archivo}`
const extDe = (archivo: string) => (archivo.split(".").pop() || "").toLowerCase()
const esImagen = (a: string) => ["jpg", "jpeg", "png", "webp", "gif"].includes(extDe(a))
const esAudio = (a: string) => ["ogg", "mp3", "m4a", "aac", "amr", "wav"].includes(extDe(a))
const esVideo = (a: string) => ["mp4", "3gp", "mov", "webm"].includes(extDe(a))
const hora = (t?: string) =>
    t ? new Date(t).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""

export default function Conversaciones() {
    const [sessions, setSessions] = useState<Session[]>([])
    const [soloPend, setSoloPend] = useState(true)
    const [loading, setLoading] = useState(false)
    const [sel, setSel] = useState<Session | null>(null)
    const [conv, setConv] = useState<Msg[]>([])
    const [convLoading, setConvLoading] = useState(false)
    const [mensaje, setMensaje] = useState("")
    const [remarks, setRemarks] = useState("")
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState("")
    const chatRef = useRef<HTMLDivElement>(null)
    useEffect(() => { chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight }) }, [conv])

    const load = useCallback(async () => {
        setLoading(true)
        try { const { data } = await AdminService.getBotSessions(soloPend); setSessions(Array.isArray(data) ? data : []) }
        catch { setSessions([]) } finally { setLoading(false) }
    }, [soloPend])

    useEffect(() => { load() }, [load])
    useEffect(() => { const t = setInterval(load, 20000); return () => clearInterval(t) }, [load])

    const openConv = useCallback(async (s: Session) => {
        setSel(s); setConvLoading(true)
        try { const { data } = await AdminService.getBotConversation(s.session_id); setConv(Array.isArray(data) ? data : []) }
        catch { setConv([]) } finally { setConvLoading(false) }
    }, [])

    const responder = async () => {
        if (!sel || !mensaje.trim()) return
        setBusy(true); setError("")
        try { await AdminService.botResponder(sel.session_id, mensaje.trim()); setMensaje(""); await openConv(sel) }
        catch (e: any) {
            // En WhatsApp el envio puede fallar (ventana de 24h vencida, token,
            // etc.). El mensaje queda en el historial pero el pasajero NO lo
            // recibio: hay que decirlo, no fallar en silencio.
            setError(typeof e?.response?.data === "string" ? e.response.data
                : "No se pudo enviar el mensaje.")
            await openConv(sel)
        }
        finally { setBusy(false) }
    }
    const finalizar = async () => {
        if (!sel) return
        setBusy(true)
        try { await AdminService.botFinalizar(sel.session_id, undefined, remarks.trim() || undefined); setRemarks(""); await load(); await openConv({ ...sel, requiere_humano: false }) }
        catch {} finally { setBusy(false) }
    }
    const archivar = async (e: React.MouseEvent, session_id: string) => {
        e.stopPropagation()
        setSessions(prev => prev.filter(s => s.session_id !== session_id))
        if (sel?.session_id === session_id) { setSel(null); setConv([]) }
        try { await AdminService.archivarBotSesion(session_id) } catch { load() }
    }

    const fecha = (d?: string) => d ? new Date(d).toLocaleString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""
    const esWa = (s: Session) => s.origen === "whatsapp"
    // En WhatsApp no hay correo: el contacto util es el telefono (el wa_id).
    const contacto = (s: Session) => esWa(s) ? (s.wa_id ? `+${s.wa_id}` : "WhatsApp") : (s.correo || "—")

    return (
        <Wrap className={sel ? "con-detalle" : ""}>
            <List>
                <ListHead>
                    <label><input type="checkbox" checked={soloPend} onChange={e => setSoloPend(e.target.checked)} /> Solo pendientes</label>
                    <button onClick={load} title="Refrescar">&#8635;</button>
                </ListHead>
                {loading && <Empty>Cargando…</Empty>}
                {!loading && sessions.length === 0 && <Empty>Sin conversaciones</Empty>}
                {sessions.map(s => (
                    <Card key={s.session_id} className={sel?.session_id === s.session_id ? "sel" : ""} onClick={() => openConv(s)}>
                        <div className="top">
                            <strong>{s.nombre || "Anónimo"}</strong>
                            <span className="paso">Paso {s.paso_actual}</span>
                        </div>
                        <div className="sub">
                            <span className={esWa(s) ? "canal wa" : "canal"}>{esWa(s) ? "WhatsApp" : "Web"}</span>
                            {contacto(s)}
                        </div>
                        {s.requiere_humano && <div className="alert">⚠ {s.motivo_escalada || "Pendiente de intervención"}</div>}
                        <div className="meta">{fecha(s.fecha_ultimo_msg)}{s.atendida_por ? ` · ${s.atendida_por}` : ""}</div>
                        <button className="arch" title="Archivar conversación" onClick={(e) => archivar(e, s.session_id)}>Archivar</button>
                    </Card>
                ))}
            </List>
            <Detail>
                {!sel && <Empty>Seleccioná una conversación</Empty>}
                {sel && (
                    <>
                        <DetailHead>
                            <div>
                                {/* Solo se ve en pantalla angosta, donde la lista y la
                                    conversación no caben juntas. */}
                                <button className="volver" onClick={() => setSel(null)}>←</button>
                                <strong>{sel.nombre || "Anónimo"}</strong> · {contacto(sel)}
                                {esWa(sel) && <Badge className="wa">WhatsApp</Badge>}
                            </div>
                            {sel.requiere_humano ? <Badge className="pend">Pendiente</Badge> : <Badge>Bot atendiendo</Badge>}
                        </DetailHead>
                        <Chat ref={chatRef}>
                            {convLoading && <Empty>Cargando…</Empty>}
                            {conv.map((m, i) => (
                                <Bubble key={i} className={m.role}>
                                    {m.role === "human_agent" && <em>{m.nombre_agente || "Agente"}</em>}
                                    {/* Instruccion interna: el pasajero NUNCA la vio. La dejo un
                                        supervisor por WhatsApp y el bot la aplica sin mencionarla. */}
                                    {m.role === "supervisor" && (
                                        <em>Instrucción interna de {m.nombre_agente || "el equipo"}
                                            {" · el pasajero no la ve"}</em>
                                    )}
                                    <span>{m.contenido}</span>
                                    {m.archivo && (
                                        <div className="adj">
                                            {esImagen(m.archivo) && (
                                                <a href={urlArchivo(m.archivo)} target="_blank" rel="noreferrer">
                                                    <img src={urlArchivo(m.archivo)} alt="Archivo enviado por el pasajero" />
                                                </a>
                                            )}
                                            {esAudio(m.archivo) && <audio controls preload="none" src={urlArchivo(m.archivo)} />}
                                            {esVideo(m.archivo) && <video controls preload="metadata" src={urlArchivo(m.archivo)} />}
                                            <a className="dl" href={urlArchivo(m.archivo)} target="_blank" rel="noreferrer">
                                                {esImagen(m.archivo) || esAudio(m.archivo) || esVideo(m.archivo) ? "Abrir en pestaña nueva" : "Descargar archivo"}
                                            </a>
                                        </div>
                                    )}
                                    {m.timestamp && <time>{hora(m.timestamp)}</time>}
                                </Bubble>
                            ))}
                        </Chat>
                        <Intervene>
                            {error && <ErrorMsg>{error}</ErrorMsg>}
                            <textarea
                                placeholder={esWa(sel)
                                    ? "Responder por WhatsApp (le llega al pasajero a su celular)…"
                                    : "Responder como agente (se muestra al pasajero con tu nombre)…"}
                                value={mensaje} onChange={e => setMensaje(e.target.value)} />
                            <div className="row">
                                <button disabled={busy || !mensaje.trim()} onClick={responder}>Responder</button>
                                <input placeholder="Remarks operativos (opcional)" value={remarks} onChange={e => setRemarks(e.target.value)} />
                                <button className="fin" disabled={busy} onClick={finalizar}>Finalizar intervención</button>
                            </div>
                        </Intervene>
                    </>
                )}
            </Detail>
        </Wrap>
    )
}

// En el celular no caben la lista y la conversación al mismo tiempo: se muestra
// una sola, y al elegir una conversación se cambia a ella con un botón de volver.
// Mismo corte que styles/responsive.ts (mobile), que es donde la barra lateral
// del admin pasa a superponerse y le deja todo el ancho al contenido.
const ANGOSTO = "@media only screen and (max-width: 870px)"
const Wrap = styled.div`
display:flex;gap:14px;height:100%;overflow:hidden;
${ANGOSTO}{
  gap:0;
  &.con-detalle > .lista{display:none;}
  &:not(.con-detalle) > .detalle{display:none;}
}
`
const List = styled.div.attrs({ className: "lista" })`
width:300px;flex-shrink:0;overflow-y:auto;display:flex;flex-direction:column;gap:8px;
${ANGOSTO}{width:100%;}
`
const ListHead = styled.div`
display:flex;justify-content:space-between;align-items:center;font-size:13px;color:#8B7355;
label{display:flex;gap:6px;align-items:center;cursor:pointer;}
button{border:none;background:#f2ebe0;border-radius:8px;width:28px;height:28px;cursor:pointer;font-size:15px;}
`
const Card = styled.div`
border:1px solid #e3ddd2;border-radius:10px;padding:10px 12px;cursor:pointer;background:#fff;
&.sel{border-color:#C4622D;box-shadow:0 0 0 1px #C4622D;}
.top{display:flex;justify-content:space-between;align-items:center;}
.top strong{font-size:14px;color:#2D2318;}
.paso{font-size:11px;background:#f2ebe0;color:#8B7355;padding:2px 8px;border-radius:10px;}
.sub{font-size:12px;color:#8B7355;margin-top:2px;display:flex;gap:6px;align-items:center;}
.canal{font-size:10px;padding:1px 6px;border-radius:8px;background:#eee;color:#666;flex-shrink:0;}
.canal.wa{background:#dcf8c6;color:#25632f;}
.alert{font-size:12px;color:#9E4A1E;margin-top:6px;}
.meta{font-size:11px;color:#A09070;margin-top:6px;}
.arch{margin-top:8px;border:none;background:#f2ebe0;color:#8B7355;border-radius:8px;padding:4px 10px;cursor:pointer;font-size:11px;}
.arch:hover{background:#e8ddc9;}
`
const Detail = styled.div.attrs({ className: "detalle" })`
flex:1;min-width:0;display:flex;flex-direction:column;border:1px solid #e3ddd2;border-radius:10px;overflow:hidden;
${ANGOSTO}{border:none;border-radius:0;}
`
const DetailHead = styled.div`
display:flex;justify-content:space-between;align-items:center;gap:8px;padding:10px 14px;border-bottom:1px solid #e3ddd2;font-size:13px;color:#2D2318;
> div{min-width:0;overflow-wrap:anywhere;}
.volver{display:none;border:none;background:#f2ebe0;color:#8B7355;border-radius:8px;width:30px;height:30px;font-size:16px;cursor:pointer;margin-right:8px;vertical-align:middle;}
${ANGOSTO}{.volver{display:inline-block;}}
`
const Badge = styled.span`font-size:11px;padding:3px 10px;border-radius:10px;background:#e2efda;color:#3c6b2f;&.pend{background:#fce4d6;color:#9E4A1E;}&.wa{background:#dcf8c6;color:#25632f;margin-left:8px;}`
const ErrorMsg = styled.div`font-size:12px;color:#9E4A1E;background:#fce4d6;border-radius:8px;padding:6px 10px;`
const Chat = styled.div`flex:1;overflow-y:auto;padding:14px;display:flex;flex-direction:column;gap:8px;background:#faf7f1;`
const Bubble = styled.div`
max-width:78%;padding:8px 12px;border-radius:12px;font-size:13px;line-height:1.5;white-space:pre-wrap;overflow-wrap:anywhere;
time{display:block;font-size:10px;opacity:.65;margin-top:3px;text-align:right;white-space:nowrap;}
.adj{margin-top:6px;display:flex;flex-direction:column;gap:4px;}
.adj img{max-width:100%;max-height:260px;border-radius:8px;display:block;cursor:zoom-in;}
.adj audio,.adj video{max-width:100%;}
.adj .dl{font-size:11px;color:inherit;opacity:.8;text-decoration:underline;}
${ANGOSTO}{max-width:92%;}
em{display:block;font-size:11px;color:#9E4A1E;margin-bottom:2px;font-style:normal;font-weight:600;}
&.user{align-self:flex-end;background:#C4622D;color:#fff;}
&.assistant{align-self:flex-start;background:#fff;border:1px solid #e3ddd2;color:#2D2318;}
&.human_agent{align-self:flex-start;background:#fff4ec;border:1px solid #f0c9b0;color:#2D2318;}
&.system{align-self:center;background:#eee;color:#666;font-size:12px;}
&.supervisor{align-self:center;max-width:92%;background:#fffbe6;border:1px dashed #d9c48a;color:#6b5b24;font-size:12px;}
&.supervisor em{color:#8a7320;}
`
const Intervene = styled.div`
border-top:1px solid #e3ddd2;padding:10px 12px;display:flex;flex-direction:column;gap:8px;background:#fff;
${ANGOSTO}{.row{flex-wrap:wrap;} .row input{flex:1 1 100%;order:3;}}
textarea{width:100%;min-height:48px;resize:vertical;border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-family:inherit;font-size:13px;box-sizing:border-box;}
.row{display:flex;gap:8px;align-items:center;}
.row input{flex:1;border:1px solid #d9cfbf;border-radius:8px;padding:8px;font-size:13px;}
.row button{border:none;border-radius:8px;padding:9px 14px;background:#C4622D;color:#fff;cursor:pointer;font-size:13px;}
.row button.fin{background:#8B7355;}
.row button:disabled{opacity:.5;cursor:default;}
`
const Empty = styled.div`color:#A09070;font-size:13px;text-align:center;padding:24px;margin:auto;`
